import { ReactNode, useCallback, useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { AnimatePresence, motion } from 'framer-motion';
import { camelCase } from 'lodash-es';
import { useQuery } from 'react-query';

import { Box, Card, Flex, Heading, SimpleGrid } from '@chakra-ui/react';
import { headerHeight } from '@udacity/udacity-header-v2';

import { Alert } from '~/components/alert';
import { ContentContainer } from '~/components/container';
import { Link } from '~/components/link';
import { trackEvent } from '~/features/analytics/track-event';
import { getFormalDegreeKey } from '~/features/analytics/utils/get-formal-degree-key';
import { getCatalogSearchResults } from '~/features/catalog/controllers/get-catalog-search-results';
import { CatalogSearchResults } from '~/features/catalog/models/catalog-search-result';
import { filtersSchema, sortNameSchema, UriFilter } from '~/features/catalog/models/filter';
import { updateCatalogUrlState } from '~/features/catalog/utils/catalog-url-state-utils';
import { getPageNumber } from '~/features/catalog/utils/get-page-number';
import { isInitialView } from '~/features/catalog/utils/is-initial-view';
import { useFavorites } from '~/features/favorites/hooks/use-favorites';
import { useEnrollmentsProgress } from '~/features/program/hooks/use-enrollments-progress';
import { CatalogStructuredData } from '~/features/seo/components/catalog-structured-data';
import { useCatalogPath } from '~/hooks/use-catalog-path';
import { useI18n } from '~/hooks/use-i18n';

import { ChevronLeftIcon, FilterControlsIcon } from '../icons';

import { CatalogCard, CatalogCardSkeleton } from './catalog-card';
import { CatalogFilterChips } from './catalog-filter-chips';
import { CatalogFilters } from './catalog-filters';
import { CatalogNoResults } from './catalog-no-results';
import { CatalogSort } from './catalog-sort';
import { PaginationWithLinks } from './pagination-with-links';

const DEFAULT_ALL_FILTERS: Record<string, string> = {
  scope: 'all',
  language: 'all',
} as const;

export function Catalog({
  initialCatalogSearchResults,
  schoolUriFilters,
  skillUriFilters,
  keysToFilterBy,
  showBackToAllProgramsLink,
  filterHeaderSlot,
}: {
  initialCatalogSearchResults: CatalogSearchResults | null;
  schoolUriFilters: UriFilter[];
  skillUriFilters: UriFilter[];
  keysToFilterBy?: string[];
  showBackToAllProgramsLink?: boolean;
  filterHeaderSlot?: ReactNode;
}) {
  const router = useRouter();
  const { t, isTranslated } = useI18n();
  const { favorites, favoritesIsFetched } = useFavorites();
  const { enrollmentsProgressIsLoading, enrollmentsProgress, enrollmentsProgressIsFetched } = useEnrollmentsProgress();

  const { selectedFilters, selectedSort, page, searchValue } = useMemo(() => {
    const {
      scope,
      price,
      school,
      skill,
      difficulty,
      duration,
      rawDuration,
      type,
      availability,
      language,
      sort: sortParam,
      page: pageCount,
    } = router.query;
    const sort = sortNameSchema.parse(sortParam);

    return {
      selectedFilters: filtersSchema.parse({
        scope,
        price,
        school,
        skill,
        difficulty,
        duration,
        rawDuration,
        type,
        availability,
        // Only apply the language filter when the user's language has translations
        language: isTranslated ? language ?? DEFAULT_ALL_FILTERS.language : undefined,
      }),
      selectedSort: sort,
      page: getPageNumber(pageCount),
      searchValue: router.query.searchValue as string | undefined,
    };
  }, [router.query, isTranslated]);

  /**
   * initialCatalogSearchResultsWithCatalogSearchItems is fetched in getServerSideProps
   * react-query will cache the initial data, for every key, unless we explicitly tell it not to.
   * So we check to see if the selected filters, sort, page, and searchValue are the initial values, and if so it will use the initial data.
   * ref: https://github.com/TanStack/query/discussions/1674#discussioncomment-304189
   */

  const catalogSearchKey = ['catalog', selectedFilters, selectedSort, page, searchValue, keysToFilterBy?.join('-')];
  const favoriteFilterSelected = selectedFilters.scope?.[0] === 'favorite';
  const enrolledFilterSelected = selectedFilters.scope?.[0] === 'enrolled';
  const completedFilterSelected = selectedFilters.scope?.[0] === 'completed';

  const companySlug = router.query.companySlug as string | undefined;
  const catalogPath = useCatalogPath();
  // Determine if the catalog search query should be enabled.
  // If the 'completed' filter is selected, wait until enrollments data is fetched.
  // If the 'favorite' filter is selected, wait until favorites data is fetched.
  const shouldWaitForEnrollments = completedFilterSelected && !enrollmentsProgressIsFetched;
  const shouldWaitForFavorites = favoriteFilterSelected && !favoritesIsFetched;

  const completedKeys =
    enrollmentsProgress?.programs
      .filter((enrollmentProgress) => enrollmentProgress.complete && enrollmentProgress.completion === 100)
      .map((enrollmentProgress) => enrollmentProgress.key) ?? [];
  const completedKeysToFilterBy = keysToFilterBy
    ? keysToFilterBy.filter((enrollment) => completedKeys.includes(enrollment))
    : completedKeys;

  const favoritesToFilterBy = keysToFilterBy
    ? favorites?.filter((programKey) => keysToFilterBy.includes(programKey))
    : favorites;

  const isCatalogSearchEnabled =
    !shouldWaitForEnrollments &&
    !shouldWaitForFavorites &&
    (completedFilterSelected ? completedKeysToFilterBy.length > 0 : true) &&
    (favoriteFilterSelected ? !!favoritesToFilterBy?.length : true);

  const {
    data: catalogSearchResultsItems,
    isFetching: catalogSearchResultsItemsIsLoading,
    refetch: refetchCatalogSearchResults,
  } = useQuery<CatalogSearchResults>(
    catalogSearchKey,
    async () => {
      let scopedKeysToFilterBy: string[] | undefined = keysToFilterBy;

      if (completedFilterSelected) {
        scopedKeysToFilterBy = completedKeysToFilterBy;
      }

      if (favoriteFilterSelected) {
        scopedKeysToFilterBy = favoritesToFilterBy;
      }

      return await getCatalogSearchResults({
        selectedFilters,
        selectedSort,
        page,
        schoolUriFilters,
        skillUriFilters,
        searchValue,
        keysToFilterBy: scopedKeysToFilterBy,
        companySlug,
        shouldFetchEnrolledOnly: enrolledFilterSelected || completedFilterSelected,
      });
    },
    {
      enabled: isCatalogSearchEnabled,
      initialData: () => {
        return isInitialView(router.query, searchValue) ? initialCatalogSearchResults ?? undefined : undefined;
      },
    }
  );

  const totalPages = catalogSearchResultsItems?.numberOfPages ?? 0;

  // Note: to prevent refetch from happening every time the length of the favorites changes, we need to do a boolean check outside of the useEffect and use that instead.
  const shouldRefetchFavorites = favoriteFilterSelected && !!favoritesToFilterBy?.length;
  useEffect(() => {
    if (shouldRefetchFavorites) {
      refetchCatalogSearchResults();
    }
  }, [shouldRefetchFavorites, refetchCatalogSearchResults]);

  const numberOfSelectedFilters = Object.entries(selectedFilters).filter(
    ([filterType, filterValues]) =>
      filterValues &&
      filterValues.length > 0 &&
      // Exclude filters set to their default value 'all'
      !filterValues.includes(DEFAULT_ALL_FILTERS[filterType])
  ).length;
  const isLoading = catalogSearchResultsItemsIsLoading || enrollmentsProgressIsLoading;

  const titleText = useMemo(() => {
    if (searchValue) {
      return t('catalog.searchResultsFor', { query: searchValue });
    }

    let filterNames = [];
    let endText = t('catalog.programs');

    for (const filterValues of Object.values({
      scope: selectedFilters.scope,
      price: selectedFilters.price,
      difficulty: selectedFilters.difficulty,
      school: selectedFilters.school,
    })) {
      if (filterValues && filterValues?.length >= 1) {
        filterNames.push(t(`catalog.filters.option.${camelCase(filterValues[0])}`));
      }
    }

    if (selectedFilters.type?.length === 1) {
      const type = selectedFilters.type[0];
      switch (type) {
        case 'course':
          endText = t('catalog.allCourses');
          break;
        case 'nanodegree program':
          endText = t('catalog.allNanodegrees');
          break;
        case 'masters':
          endText = t('catalog.allMastersPrograms');
          break;
        default:
          break;
      }
    }

    return `${Boolean(filterNames.length) ? filterNames.join(' ') : t('catalog.filters.option.all')} ${endText}`;
  }, [searchValue, selectedFilters, t]);

  const handlePageChange = useCallback(
    (page: number) => {
      updateCatalogUrlState({
        router: router,
        updatedRouteParameters: {
          page: page === 0 ? undefined : `page-${page + 1}`,
        },
      });
    },
    [router]
  );

  const enrollmentScopes = ['enrolled', 'completed'];

  return (
    <>
      <ContentContainer contentMaxWidth='content-max-width-lg' isolation='isolate'>
        {(searchValue || showBackToAllProgramsLink) && (
          <Box
            paddingY={{
              base: '12px',
              // md: '24px',
            }}
          >
            <Link _visited={{ color: '' }} href={catalogPath}>
              <ChevronLeftIcon h='24px' w='24px' />
              {t('catalog.backToAllPrograms')}
            </Link>
          </Box>
        )}

        <Flex flexDirection='column' gap='24px' position='relative'>
          <Flex alignItems='flex-start' as='section' flexDirection={{ base: 'column', lg: 'row' }} gap={6}>
            <Box
              display={{ base: 'none', lg: 'block' }}
              flexShrink={0}
              maxHeight='calc(100vh - 164px)'
              overflowY='auto'
              position='sticky'
              top={headerHeight}
              width='256px'
              sx={{
                scrollbarWidth: 'thin',
                scrollbarColor: '#DFDFDF transparent',
              }}
            >
              <CatalogFilters
                isLoading={catalogSearchResultsItemsIsLoading}
                numberOfResults={catalogSearchResultsItems?.numberOfHits}
                numberOfSelectedFilters={numberOfSelectedFilters}
                selectedFilters={selectedFilters}
                skillUriFilters={skillUriFilters}
              />
            </Box>
            <Flex
              flex={1}
              flexDirection='column'
              width='100%'
              gap={{
                base: 6,
                md: 'unset',
              }}
            >
              <Box
                bgColor='grey.50'
                overflow='hidden'
                position='sticky'
                top={headerHeight}
                zIndex={5}
                paddingY={{
                  base: '12px',
                  md: '24px',
                }}
              >
                <Card
                  border='1px solid'
                  borderColor='grey.100'
                  borderRadius='8px'
                  boxShadow='none'
                  padding='12px 12px 12px 24px'
                >
                  <Flex
                    alignItems='center'
                    as='header'
                    display={{ base: 'none', lg: 'flex' }}
                    gap={8}
                    justifyContent='space-between'
                  >
                    <Box>
                      {filterHeaderSlot ? (
                        filterHeaderSlot
                      ) : (
                        <>
                          <Heading
                            as='h1'
                            color='grey.900'
                            fontSize='1.125rem'
                            fontWeight='600'
                            lineHeight='1.1'
                            size='h3'
                            textTransform='capitalize'
                          >
                            {titleText}
                          </Heading>
                          {isTranslated && searchValue && (
                            <Alert fontSize='sm' marginTop='8px' status='info'>
                              {t('catalog.translatedResultsAlert')}
                            </Alert>
                          )}
                        </>
                      )}
                    </Box>
                    <Box flexShrink={0}>
                      <CatalogSort />
                    </Box>
                  </Flex>
                  {numberOfSelectedFilters > 0 && (
                    <Flex
                      alignItems='center'
                      color='grey.500'
                      flexWrap='wrap'
                      fontSize='0.8125rem'
                      gap='6px'
                      lineHeight='1.4'
                      marginTop='8px'
                    >
                      <Box whiteSpace='nowrap'>
                        <FilterControlsIcon height='16px' width='16px' /> {t('catalog.filters.activeFilters')}
                      </Box>
                      <CatalogFilterChips selectedFilters={selectedFilters} />
                    </Flex>
                  )}
                  <Box display={{ base: 'block', lg: 'none' }}>
                    <Flex alignItems='center' as='header' gap='24px' justifyContent='space-between' py='16px'>
                      {filterHeaderSlot ? (
                        filterHeaderSlot
                      ) : (
                        <Heading
                          as='h1'
                          color='grey.900'
                          fontSize='1.125rem'
                          fontWeight='600'
                          lineHeight='1.1'
                          textTransform='capitalize'
                        >
                          {titleText}
                        </Heading>
                      )}
                      <CatalogFilters
                        isLoading={catalogSearchResultsItemsIsLoading}
                        numberOfResults={catalogSearchResultsItems?.numberOfHits}
                        numberOfSelectedFilters={numberOfSelectedFilters}
                        selectedFilters={selectedFilters}
                        skillUriFilters={skillUriFilters}
                      />
                    </Flex>
                    {!filterHeaderSlot && isTranslated && searchValue && (
                      <Alert fontSize='sm' marginTop='8px' status='info'>
                        {t('catalog.translatedResultsAlert')}
                      </Alert>
                    )}
                  </Box>
                </Card>
              </Box>
              <AnimatePresence initial={false} mode='popLayout'>
                {/* If not loading and there are results */}
                {!isLoading && Boolean(catalogSearchResultsItems?.searchResultItems?.length) && (
                  <Box>
                    <Box
                      key='catalog-cards'
                      animate={{ opacity: 1 }}
                      as={motion.div}
                      exit={{ opacity: 0 }}
                      initial={{ opacity: 0 }}
                    >
                      <SimpleGrid columns={{ base: 2, md: 3 }} spacing={{ base: '18px', lg: '24px' }}>
                        {catalogSearchResultsItems?.searchResultItems?.map((searchResultItems) => (
                          <CatalogCard
                            key={searchResultItems.key}
                            catalogItem={searchResultItems}
                            width='100%'
                            priority
                            onClick={() => {
                              const formalDegreeKey = getFormalDegreeKey(searchResultItems);
                              trackEvent({
                                type: 'click',
                                name: 'catalogCtaClicked',
                                label: searchResultItems.title!,
                                action: 'program_card_clicked',
                                ...(formalDegreeKey && {
                                  additionalAttributes: {
                                    formal_degree_key: formalDegreeKey,
                                  },
                                }),
                              });
                            }}
                          />
                        ))}
                      </SimpleGrid>
                    </Box>
                  </Box>
                )}
                {/* If loading */}
                {isLoading && (
                  <Box
                    key='catalog-card-skeletons'
                    animate={{ opacity: 1 }}
                    as={motion.div}
                    exit={{ opacity: 0 }}
                    initial={{ opacity: 0 }}
                  >
                    <SimpleGrid columns={{ base: 2, md: 3 }} flex={1} spacing={{ base: '8px', lg: '24px' }}>
                      {[...Array(24)].map((_, i) => (
                        <CatalogCardSkeleton
                          key={i}
                          type={enrollmentScopes.includes(selectedFilters.scope?.[0] ?? '') ? 'enrollment' : 'catalog'}
                          width='100%'
                        />
                      ))}
                    </SimpleGrid>
                  </Box>
                )}
                {/* If not loading and no results */}
                {!isLoading && !Boolean(catalogSearchResultsItems?.searchResultItems?.length) && (
                  <Box
                    key='catalog-no-results'
                    animate={{ opacity: 1 }}
                    as={motion.div}
                    exit={{ opacity: 0 }}
                    flex={1}
                    initial={{ opacity: 0 }}
                    width='100%'
                  >
                    <CatalogNoResults />
                  </Box>
                )}
              </AnimatePresence>
              <Flex justifyContent='center' mt='40px'>
                <PaginationWithLinks
                  currentPage={page}
                  totalPages={totalPages}
                  pageIsZeroIndexed
                  onChange={handlePageChange}
                />
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </ContentContainer>
      <CatalogStructuredData catalogSearchResults={catalogSearchResultsItems} />
    </>
  );
}
